import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useRouteContext,
  useRouter,
} from '@tanstack/react-router';
import { Flex } from 'antd';
import { useCallback, useEffect } from 'react';

import { IMPERSONATION_FLAG, getAppAuth } from '../../../api/axios.instance';
import { useRecordAccountImpersonationActiveOrganization } from '../../../api/generated/api';
import { AccountControl } from '../../../modules/account/components/account-control';
import { ImpersonationBanner } from '../../../modules/account/components/impersonation-banner/ImpersonationBanner';
import { usePermissions } from '../../../modules/account/hooks';
import { AiAssistantBar } from '../../../modules/ai-next/components';
import { AiAssistantProvider } from '../../../modules/ai-next/providers';
import { signOutWithRevoke } from '../../../modules/auth/sign-out';
import {
  BILLING_GATE_STRATEGY,
  BillingAlertsBar,
  BillingUpgradeBanner,
  SubscribeRequiredModal,
} from '../../../modules/billing';
import { ActiveCallBar } from '../../../modules/communications/components/active-call-bar';
import { CallProvider } from '../../../modules/communications/providers/CallProvider';
import { GlobalSearch } from '../../../modules/global-search/components';
import { ImportProgressToast } from '../../../modules/import';
import { HeaderActions } from '../../../modules/navigation/components/header-actions';
import { useRealtimeEvents } from '../../../modules/realtime';
import { DashboardLayout } from '../../../ui/components/layout';
import {
  NavigationMenu,
  NavigationModal,
  SideNavigation,
  TopNavigation,
} from '../../../ui/components/navigation';
import { useNavigationPosition } from '../../../ui/hooks';
import { storage } from '../../../ui/utils/local-storage';

import type { GetAccountResponseDto } from '../../../api/generated/model';

const AppLayout = () => {
  const { account, selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });

  // Setup realtime events listener for the selected organization
  useRealtimeEvents(selectedOrganization.id);

  const { can } = usePermissions();
  const canViewRecords = can('records', 'view');
  const canViewDeals = can('deals', 'view');
  const canViewAgents = can('agents', 'view');
  const canViewAnalytics = can('analytics', 'view');

  const navigate = useNavigate();
  const router = useRouter();
  const { mutate: recordImpersonationActiveOrganization } =
    useRecordAccountImpersonationActiveOrganization({ mutation: { retry: false } });
  const location = useLocation();
  const pathname = location.pathname;

  const { isSideNav } = useNavigationPosition();
  const isTableRoute = pathname.startsWith('/records') || pathname.startsWith('/pipeline');

  const tablePlaceholder = pathname.startsWith('/records')
    ? 'Search records'
    : pathname.startsWith('/pipeline')
      ? 'Search pipelines'
      : undefined;

  const tableSearch = isTableRoute
    ? {
        value: location.search?.search ?? '',
        onChange: (val: string) => {
          router.navigate({
            search: ((prev: Record<string, unknown>) => ({
              ...prev,
              search: val || undefined,
            })) as never,
            replace: true,
            resetScroll: false,
          });
        },
        placeholder: tablePlaceholder,
      }
    : undefined;

  const handleSelectOrganization = useCallback(
    async (organizationId: string) => {
      if (
        sessionStorage.getItem(IMPERSONATION_FLAG) === '1' &&
        organizationId !== selectedOrganization.id
      ) {
        recordImpersonationActiveOrganization({ data: { organizationId } });
      }

      const map = storage.getJSON<Record<string, string>>('selectedOrganizationId', {});

      map[account.id] = organizationId;

      storage.setJSON('selectedOrganizationId', map);

      await router.navigate({ to: '/records' });
      router.invalidate();
    },
    [router, account.id, selectedOrganization.id, recordImpersonationActiveOrganization],
  );

  const handleSignOut = useCallback(async () => {
    await signOutWithRevoke(getAppAuth());
    router.invalidate();
  }, [router]);

  useEffect(() => {
    if (pathname.startsWith('/settings')) {
      return;
    }
    const needsRecordsView = pathname.startsWith('/records') || pathname.startsWith('/leads');
    const needsDealsView = pathname.startsWith('/pipeline');
    const needsAgentsView = pathname.startsWith('/agents');
    const needsAnalyticsView = pathname.startsWith('/analytics');
    const hasAccess =
      (needsRecordsView && canViewRecords) ||
      (needsDealsView && canViewDeals) ||
      (needsAgentsView && canViewAgents) ||
      (needsAnalyticsView && canViewAnalytics) ||
      (!needsRecordsView && !needsDealsView && !needsAgentsView && !needsAnalyticsView);
    if (!hasAccess) {
      const fallback = '/records';
      navigate({ to: fallback });
    }
  }, [pathname, canViewRecords, canViewDeals, canViewAgents, canViewAnalytics, navigate]);

  const isAgentBuilder =
    pathname.startsWith('/agents/new') ||
    (pathname.startsWith('/agents/') && pathname !== '/agents/');

  const renderHeaderControls = (compact: boolean) => (
    <Flex align="center" gap={8}>
      <HeaderActions organizationId={selectedOrganization.id} variant="top" />
      <AccountControl
        account={account}
        selectedOrganization={selectedOrganization}
        onSelectOrganization={handleSelectOrganization}
        settingsTo="/settings"
        onSignOut={handleSignOut}
        compact={compact}
      />
    </Flex>
  );

  return (
    <AiAssistantProvider organizationMembership={selectedMembership}>
      <CallProvider organizationId={selectedOrganization.id}>
        <DashboardLayout
          header={
            !isSideNav && (
              <TopNavigation
                left={
                  <GlobalSearch
                    organizationId={selectedOrganization.id}
                    tableSearch={tableSearch}
                  />
                }
                right={(isMobile) => renderHeaderControls(isMobile)}
              >
                {(isMobile) => <NavigationMenu mode={isMobile ? 'side' : 'top'} />}
              </TopNavigation>
            )
          }
          sidebar={
            isSideNav && (
              <SideNavigation
                top={(collapsed) => (
                  <AccountControl
                    account={account}
                    selectedOrganization={selectedOrganization}
                    onSelectOrganization={handleSelectOrganization}
                    settingsTo="/settings"
                    onSignOut={handleSignOut}
                    compact={collapsed}
                  />
                )}
              >
                {(collapsed) => <NavigationMenu mode="side" collapsed={collapsed} />}
              </SideNavigation>
            )
          }
          banner={<ActiveCallBar />}
          subHeader={
            <>
              <ImpersonationBanner />
              <BillingUpgradeBanner />
              <BillingAlertsBar />
              <SubscribeRequiredModal />
              <NavigationModal />
            </>
          }
          footer={!isAgentBuilder ? <AiAssistantBar /> : undefined}
        >
          <Outlet key={selectedOrganization.id} />
          <ImportProgressToast organizationId={selectedOrganization.id} />
        </DashboardLayout>
      </CallProvider>
    </AiAssistantProvider>
  );
};

export const Route = createFileRoute('/_authenticated/_app')({
  beforeLoad: (options: { context: { account: GetAccountResponseDto } }) => {
    const { account } = options.context;

    if (account.role === 'superadmin') {
      throw redirect({ to: '/superadmin' });
    }

    if (account.role !== 'user') {
      throw new Error('Failed to determine account role');
    }

    if (!account.memberships?.length) {
      throw redirect({ to: '/onboarding/$step', params: { step: 'profile' } });
    }

    const isPlaceholderName = account.firstName === 'New' && account.lastName === 'User';
    const profileComplete = Boolean(account.firstName && account.lastName) && !isPlaceholderName;
    if (!profileComplete) {
      throw redirect({ to: '/onboarding/$step', params: { step: 'profile' } });
    }

    const selectedOrganizationId = storage.getJSON<Record<string, string>>(
      'selectedOrganizationId',
      {},
    )[account.id] as string | undefined;

    const selectedMembership =
      account.memberships?.find(
        (membership) => membership.organization.id === selectedOrganizationId,
      ) ?? account.memberships?.[0];

    if (!selectedMembership) {
      throw redirect({ to: '/onboarding/$step', params: { step: 'profile' } });
    }

    const { onboarding, isOwner } = selectedMembership.organization;
    if (onboarding && !onboarding.completedAt && isOwner) {
      throw redirect({ to: '/onboarding/$step', params: { step: onboarding.currentStep } });
    }

    const skipBillingGate = import.meta.env.VITE_SKIP_BILLING_GATE === 'true';
    if (
      BILLING_GATE_STRATEGY === 'onboarding' &&
      !skipBillingGate &&
      !selectedMembership.organization.hasActiveSubscription
    ) {
      throw redirect({ to: '/onboarding/billing' });
    }

    return {
      selectedOrganization: {
        id: selectedMembership.organization.id,
        name: selectedMembership.organization.name,
        avatarIndex: selectedMembership.organization.avatarIndex,
      },
      selectedMembership,
      permissions: selectedMembership.role.permissions,
    };
  },
  component: AppLayout,
});
